/**
 * Cosmic Aurora Visualizer - Procedural deep-space starfield with beat-reactive effects
 *
 * Built on techniques from wwwtyro/space-scene-2d and Delvar/nebula:
 * - Exponential brightness distribution for realistic star fields
 * - Black-body stellar colors for natural star color variation
 * - Noise-based nebula pre-rendered at 1/4 resolution with per-band frequency response
 * - Hero stars with radial gradient glow + aligned diffraction spikes (real telescope optics)
 * - Domain-distorted fBm noise for organic nebula cloud shapes
 *
 * Rendering layers (bottom to top):
 * 1. Trail fade — offscreen canvas composited at reduced opacity over fresh background
 * 2. Pre-rendered nebula layers (OUTSIDE trail) — 2 layers with hue-shift or brightness response
 * 3. Pre-rendered point star field (OUTSIDE trail) — exponential distribution, black-body colors
 * 4. Mid stars — medium particles with glow, moderate beat response
 * 5. Hero stars — radial gradient glow + aligned diffraction spikes, dramatic beat response
 * 6. Beat flash overlay — palette-colored tint wash
 * 7. Sparkle particles — tiny bright points near hero stars
 *
 * Diffraction spikes:
 * All stars share the same spike angle (simulating fixed telescope optics).
 * Spike count is configurable: 4 (Hubble-style), 6 (3-vane spider), 8 (JWST-style).
 * Spikes taper exponentially from core (bright) to tip (transparent) using
 * exp(-falloff * distance) — same technique as wwwtyro/space-scene-2d.
 * Spike visibility scales with star brightness — dim stars have no visible spikes.
 *
 * Audio mapping (scaled by audioReactivity):
 * - Sub-bass → drift speed boost
 * - Bass → hero star size pulse, nebula layer 0 response
 * - Mids → scintillation ray length, nebula layer 1 response
 * - Highs → sparkle spawn rate, twinkle speed
 * - Beats → dramatic size/ray burst, colored flash, nebula pulse
 * - Spectral flux → ray jitter
 * - Nebula response modes: hue-shift (default, smooth) or brightness (classic pulse)
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'cosmic-aurora',
  displayName: 'Cosmic Aurora',
  contextType: '2d',
};

// ─── Inline Perlin 2D noise ─────────────────────────────────────────────
// Compact implementation of improved Perlin noise with fBm accumulator.
// Used for nebula pre-rendering only (not called per-frame).

const _perm = new Uint8Array(512);
const _grad2 = [[1,1],[-1,1],[1,-1],[-1,-1],[1,0],[-1,0],[0,1],[0,-1]];

function _seedNoise(seed) {
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  let s = (seed * 2654435761) >>> 0; // Knuth's multiplicative hash
  for (let i = 255; i > 0; i--) {
    s = ((s * 1103515245 + 12345) >>> 0);
    const j = s % (i + 1);
    const tmp = p[i]; p[i] = p[j]; p[j] = tmp;
  }
  for (let i = 0; i < 512; i++) _perm[i] = p[i & 255];
}

function _fade(t) { return t * t * t * (t * (t * 6 - 15) + 10); }

function _perlin2(x, y) {
  const flx = Math.floor(x), fly = Math.floor(y);
  const xi = flx & 255, yi = fly & 255;
  const xf = x - flx, yf = y - fly;
  const u = _fade(xf), v = _fade(yf);
  const aa = _perm[_perm[xi] + yi], ab = _perm[_perm[xi] + yi + 1];
  const ba = _perm[_perm[xi + 1] + yi], bb = _perm[_perm[xi + 1] + yi + 1];
  const g00 = _grad2[aa & 7], g10 = _grad2[ba & 7];
  const g01 = _grad2[ab & 7], g11 = _grad2[bb & 7];
  const n00 = g00[0] * xf + g00[1] * yf;
  const n10 = g10[0] * (xf - 1) + g10[1] * yf;
  const n01 = g01[0] * xf + g01[1] * (yf - 1);
  const n11 = g11[0] * (xf - 1) + g11[1] * (yf - 1);
  return (n00 + u * (n10 - n00)) + v * ((n01 + u * (n11 - n01)) - (n00 + u * (n10 - n00)));
}

function _fbm2(x, y, octaves, lacunarity, gain) {
  let sum = 0, amp = 1, maxAmp = 0, freq = 1;
  for (let i = 0; i < octaves; i++) {
    sum += amp * _perlin2(x * freq, y * freq);
    maxAmp += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / maxAmp;
}

// ─── Black-body stellar color LUT ───────────────────────────────────────
// Simplified from wwwtyro's 114-entry table. Covers ~2000K (red) to ~30000K (blue).
// Each entry is [r, g, b] normalized to 0–1.
const BLACK_BODY = [
  [1.00, 0.22, 0.04],  // ~2000K deep red
  [1.00, 0.38, 0.12],  // ~2500K orange-red
  [1.00, 0.50, 0.18],  // ~3000K warm orange
  [1.00, 0.63, 0.33],  // ~3500K amber
  [1.00, 0.73, 0.47],  // ~4000K warm yellow
  [1.00, 0.82, 0.62],  // ~4500K pale yellow
  [1.00, 0.87, 0.73],  // ~5000K yellowish white
  [1.00, 0.93, 0.84],  // ~5500K warm white
  [1.00, 0.96, 0.91],  // ~6000K neutral white (Sun)
  [0.95, 0.95, 1.00],  // ~7000K cool white
  [0.87, 0.90, 1.00],  // ~8000K blue-white
  [0.78, 0.84, 1.00],  // ~10000K
  [0.68, 0.77, 1.00],  // ~15000K pale blue
  [0.58, 0.69, 1.00],  // ~20000K blue
  [0.52, 0.65, 1.00],  // ~30000K deep blue
];

// ─── Color palettes (pre-parsed RGB) ────────────────────────────────────
const PALETTES = {
  'cosmic-purple': [
    [180, 60, 255], [120, 40, 200], [80, 120, 255],
    [200, 80, 180], [140, 60, 220], [220, 160, 255],
  ],
  'nebula-fire': [
    [255, 60, 40], [255, 140, 20], [255, 200, 60],
    [200, 40, 80], [255, 100, 60], [255, 220, 140],
  ],
  'deep-ocean': [
    [20, 80, 200], [40, 180, 220], [60, 220, 180],
    [80, 140, 255], [30, 120, 180], [100, 240, 220],
  ],
  'aurora-borealis': [
    [40, 255, 120], [60, 200, 255], [120, 255, 180],
    [80, 180, 255], [160, 255, 140], [200, 255, 220],
  ],
  'galactic-neon': [
    [255, 0, 200], [0, 255, 200], [200, 0, 255],
    [255, 200, 0], [0, 200, 255], [255, 100, 0],
  ],
  'stellar-gold': [
    [255, 220, 100], [255, 180, 60], [200, 160, 255],
    [255, 240, 180], [180, 140, 60], [255, 200, 140],
  ],
};

// ─── Constants ──────────────────────────────────────────────────────────
const MAX_MID = 300;
const MAX_HERO = 40;
const MAX_STARS = MAX_MID + MAX_HERO; // 340 animated stars
const HERO_START = MAX_MID;
const MAX_SPARKLES = 200;
const MAX_SPARKLE_SPAWN = 15;
const TAU = Math.PI * 2;
const BEAT_FLASH_DECAY = 0.88;

// Spike count options: 4 = Hubble-style, 6 = 3-vane spider, 8 = JWST-style
const SPIKE_COUNT_OPTIONS = [4, 6, 8];

// Min star brightness to render diffraction spikes (dim stars have no visible spikes)
const SPIKE_BRIGHTNESS_THRESHOLD = 0.25;

// Mid and hero layer parallax
const MID_SPEED = 0.4;
const HERO_SPEED = 0.2; // slower for visual weight

class CosmicAurora extends Base2DVisualizer {
  static name = 'cosmic-aurora';
  static displayName = 'Cosmic Aurora';

  static getConfigUI() {
    return [
      // Stars
      { type: 'section', label: 'Stars' },
      { type: 'range', id: 'pointStarDensity', label: 'Background Star Density', min: 10, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'starsMid', label: 'Mid Stars', min: 20, max: 300, default: 150 },
      { type: 'range', id: 'starsHero', label: 'Hero Stars', min: 3, max: 40, default: 15 },
      { type: 'range', id: 'twinkleStrength', label: 'Twinkle Strength', min: 0, max: 100, default: 60, scale: 0.01 },
      { type: 'range', id: 'twinklePeriod', label: 'Twinkle Period', min: 10, max: 100, default: 40, scale: 0.01 },
      { type: 'range', id: 'driftSpeed', label: 'Drift Speed', min: 0, max: 100, default: 15, scale: 0.01 },

      // Diffraction Spikes
      { type: 'section', label: 'Diffraction Spikes' },
      {
        type: 'select', id: 'spikeCount', label: 'Spike Count', default: 4,
        options: [
          { value: 4, label: '4 (Hubble)' },
          { value: 6, label: '6 (3-Vane)' },
          { value: 8, label: '8 (JWST)' },
        ],
      },
      { type: 'range', id: 'spikeLength', label: 'Spike Length', min: 10, max: 100, default: 65, scale: 0.01 },
      { type: 'range', id: 'spikeAngle', label: 'Spike Angle', min: 0, max: 360, default: 45 },
      { type: 'range', id: 'spikeTaper', label: 'Spike Taper', min: 10, max: 100, default: 50, scale: 0.01 },

      // Nebula
      { type: 'section', label: 'Nebula' },
      { type: 'range', id: 'nebulaIntensity', label: 'Nebula Intensity', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'nebulaScale', label: 'Nebula Scale', min: 10, max: 80, default: 35, scale: 0.0001 },
      { type: 'range', id: 'nebulaDetail', label: 'Nebula Detail (Octaves)', min: 3, max: 8, default: 6 },
      { type: 'range', id: 'nebulaDistortion', label: 'Nebula Distortion', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'nebulaSharpness', label: 'Nebula Sharpness', min: 10, max: 100, default: 65, scale: 0.01 },
      { type: 'checkbox', id: 'showNebula', label: 'Show Nebula', default: true },

      // Colors
      { type: 'section', label: 'Colors' },
      {
        type: 'select', id: 'colorPalette', label: 'Color Palette', default: 'cosmic-purple',
        options: [
          { value: 'cosmic-purple', label: 'Cosmic Purple' },
          { value: 'nebula-fire', label: 'Nebula Fire' },
          { value: 'deep-ocean', label: 'Deep Ocean' },
          { value: 'aurora-borealis', label: 'Aurora Borealis' },
          { value: 'galactic-neon', label: 'Galactic Neon' },
          { value: 'stellar-gold', label: 'Stellar Gold' },
        ],
      },
      { type: 'range', id: 'hueDriftSpeed', label: 'Hue Drift Speed', min: 0, max: 100, default: 15, scale: 0.01 },
      { type: 'range', id: 'colorVariation', label: 'Color Variation', min: 0, max: 100, default: 70, scale: 0.01 },

      // Effects
      { type: 'section', label: 'Effects' },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 40, default: 18 },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 5, max: 50, default: 15, scale: 0.01 },
      { type: 'range', id: 'sparkleAmount', label: 'Sparkle Amount', min: 0, max: 100, default: 60, scale: 0.01 },
      { type: 'range', id: 'beatFlashIntensity', label: 'Beat Flash', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'brightness', label: 'Brightness', min: 20, max: 100, default: 80, scale: 0.01 },

      // Audio
      { type: 'section', label: 'Audio' },
      { type: 'range', id: 'audioReactivity', label: 'Audio Reactivity', min: 0, max: 100, default: 80, scale: 0.01 },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 70, scale: 0.01 },
      {
        type: 'select', id: 'nebulaResponse', label: 'Nebula Music Response', default: 'hue-shift',
        options: [
          { value: 'hue-shift', label: 'Hue Shift (smooth)' },
          { value: 'brightness', label: 'Brightness (pulse)' },
        ],
      },
    ];
  }

  getElementCount() { return 32; }

  init() {
    this.time = 0;
    this.isFirstFrame = true;

    // Beat state
    this._beatFlashAlpha = 0;
    this._beatSizeDecay = 0;
    this._beatRayDecay = 0;
    this._nebulaFlash = 0;

    // Smoothed audio (delta-clamped)
    this._sBass = 0;
    this._sMid = 0;
    this._sHigh = 0;
    this._sFlux = 0;
    this._sSubBass = 0;

    // Palette state
    this._paletteHueOffset = 0;
    this._rgb = [0, 0, 0];
    this._activePalette = PALETTES['cosmic-purple'];
    this._selectPalette();

    // Pre-rendered canvas refs
    this._trailCanvas = null;
    this._trailCtx = null;
    this._pointStarCanvas = null;
    this._pointStarGenW = 0;
    this._pointStarGenH = 0;
    // 2 base layers + 2 hue-shifted variants for hue-shift response mode
    this._nebulaCanvases = [null, null, null, null];
    this._nebulaGenW = 0;
    this._nebulaGenH = 0;
    this._nebulaSeed = (Math.random() * 1e8) | 0;

    // Sparkle pool
    this._sparkleHead = 0;

    // Allocate pools
    this._initStarPool();
    this._initSparklePool();
    this._activateStars();
  }

  // ─── Pool initialization ──────────────────────────────────────────────

  _initStarPool() {
    this.starPool = new Array(MAX_STARS);
    for (let i = 0; i < MAX_STARS; i++) {
      this.starPool[i] = {
        active: false, layer: 0,
        x: 0, y: 0,
        baseSize: 0, currentSize: 0, sizeTarget: 0,
        phase: 0, twinkleSpeed: 0, brightness: 0,
        paletteT: 0, bbIndex: 0,
        // Hero-only (rayCount no longer per-star — uses global spikeCount)
        baseRayLength: 0, rayLength: 0, rayTarget: 0,
        glowRadius: 0,
      };
    }
  }

  _initSparklePool() {
    this.sparklePool = new Array(MAX_SPARKLES);
    for (let i = 0; i < MAX_SPARKLES; i++) {
      this.sparklePool[i] = {
        active: false,
        x: 0, y: 0, vx: 0, vy: 0,
        life: 0, maxLife: 0, size: 0, alpha: 0,
        r: 255, g: 255, b: 255,
      };
    }
  }

  // ─── Star activation / randomization ──────────────────────────────────

  _activateStars() {
    const w = this.canvas.width || 1920;
    const h = this.canvas.height || 1080;
    const { starsMid, starsHero } = this.config;

    // Mid stars: indices 0 to MAX_MID-1
    for (let i = 0; i < MAX_MID; i++) {
      const star = this.starPool[i];
      if (i < starsMid) {
        if (!star.active) this._randomizeStar(star, w, h, 0);
        star.active = true;
      } else {
        star.active = false;
      }
    }

    // Hero stars: indices HERO_START to HERO_START+MAX_HERO-1
    for (let i = 0; i < MAX_HERO; i++) {
      const star = this.starPool[HERO_START + i];
      if (i < starsHero) {
        if (!star.active) this._randomizeHero(star, w, h);
        star.active = true;
      } else {
        star.active = false;
      }
    }
  }

  _randomizeStar(star, w, h, layer) {
    star.x = Math.random() * w;
    star.y = Math.random() * h;
    star.layer = layer;
    star.phase = Math.random() * TAU;
    star.twinkleSpeed = 0.3 + Math.random() * 1.5;
    // Exponential brightness: many dim, few bright (wwwtyro technique)
    star.brightness = Math.min(1, Math.log(1 - Math.random()) * -0.4);
    star.paletteT = Math.random();
    star.bbIndex = Math.floor(Math.random() * BLACK_BODY.length);
    star.baseSize = 0.8 + Math.random() * 1.8;
    star.currentSize = star.baseSize;
    star.sizeTarget = star.baseSize;
  }

  _randomizeHero(star, w, h) {
    star.x = Math.random() * w;
    star.y = Math.random() * h;
    star.layer = 1;
    star.phase = Math.random() * TAU;
    star.twinkleSpeed = 0.15 + Math.random() * 0.6;
    // Exponential brightness distribution
    star.brightness = Math.min(1, Math.log(1 - Math.random()) * -0.5);
    star.paletteT = Math.random();
    star.bbIndex = Math.floor(Math.random() * BLACK_BODY.length);
    star.baseSize = 2.0 + Math.random() * 3.5;
    star.currentSize = star.baseSize;
    star.sizeTarget = star.baseSize;

    // Ray properties — spike count and angle are global (telescope optics are fixed)
    star.baseRayLength = star.baseSize * (3.0 + star.brightness * 5.0);
    star.rayLength = star.baseRayLength;
    star.rayTarget = star.baseRayLength;
    star.glowRadius = star.baseSize * (3.0 + star.brightness * 4.0);
  }

  _selectPalette() {
    this._activePalette = PALETTES[this.config.colorPalette] || PALETTES['cosmic-purple'];
  }

  _lookupPaletteRGB(t) {
    const palette = this._activePalette;
    const len = palette.length;
    const shifted = ((t + this._paletteHueOffset) % 1 + 1) % 1;
    const pos = shifted * (len - 1);
    const idx = Math.floor(pos);
    const frac = pos - idx;
    const c0 = palette[idx];
    const c1 = palette[Math.min(idx + 1, len - 1)];
    this._rgb[0] = c0[0] + (c1[0] - c0[0]) * frac;
    this._rgb[1] = c0[1] + (c1[1] - c0[1]) * frac;
    this._rgb[2] = c0[2] + (c1[2] - c0[2]) * frac;
    return this._rgb;
  }

  _getStarColor(star) {
    // Blend black-body color with palette color based on colorVariation
    const bb = BLACK_BODY[star.bbIndex];
    const cv = this.config.colorVariation;
    if (cv < 0.05) {
      // Pure black-body
      this._rgb[0] = bb[0] * 255;
      this._rgb[1] = bb[1] * 255;
      this._rgb[2] = bb[2] * 255;
    } else {
      this._lookupPaletteRGB(star.paletteT);
      const mix = cv;
      this._rgb[0] = bb[0] * 255 * (1 - mix) + this._rgb[0] * mix;
      this._rgb[1] = bb[1] * 255 * (1 - mix) + this._rgb[1] * mix;
      this._rgb[2] = bb[2] * 255 * (1 - mix) + this._rgb[2] * mix;
    }
    return this._rgb;
  }

  // ─── Pre-rendered canvas generation ───────────────────────────────────

  _ensurePreRendered(w, h) {
    if (w < 1 || h < 1) return;
    if (this._pointStarGenW !== w || this._pointStarGenH !== h) {
      this._generatePointStars(w, h);
    }
    if (this._nebulaGenW !== w || this._nebulaGenH !== h) {
      this._generateNebula(w, h);
    }
  }

  _makeCanvas(width, height) {
    if (typeof OffscreenCanvas !== 'undefined') {
      return new OffscreenCanvas(width, height);
    }
    const c = document.createElement('canvas');
    c.width = width;
    c.height = height;
    return c;
  }

  /**
   * Pre-render background star field using exponential brightness distribution.
   * Technique from wwwtyro/space-scene-2d and Delvar/nebula.
   */
  _generatePointStars(w, h) {
    const canvas = this._pointStarCanvas = this._makeCanvas(w, h);
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);

    const density = this.config.pointStarDensity;
    const count = Math.floor(w * h * density * 0.0005);
    const brightMult = 0.35;

    for (let i = 0; i < count; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;

      // Exponential brightness: log(1-u) * -k gives many dim, few bright
      const intensity = Math.min(1, Math.log(1 - Math.random()) * -brightMult);

      // Black-body color for natural star color variation
      const bb = BLACK_BODY[Math.floor(Math.random() * BLACK_BODY.length)];
      const r = (bb[0] * intensity * 255) | 0;
      const g = (bb[1] * intensity * 255) | 0;
      const b = (bb[2] * intensity * 255) | 0;

      ctx.fillStyle = `rgba(${r},${g},${b},${intensity.toFixed(3)})`;
      ctx.fillRect(x, y, 1, 1);
    }

    this._pointStarGenW = w;
    this._pointStarGenH = h;
  }

  /**
   * Pre-render noise-based nebula at 1/4 resolution.
   * Two base layers (bass-responsive + mid-responsive) with different noise seeds.
   * For hue-shift mode: generates 2 additional hue-shifted variants (4 canvases total).
   *
   * Uses domain-distorted fBm with abs() for ridged appearance and aggressive
   * exponential falloff (wwwtyro technique: exp(-falloff * abs(noise))) to create
   * distinct cloud shapes against deep black rather than uniform fog.
   * nebulaSharpness controls the falloff exponent: higher = sharper edges, more black.
   */
  _generateNebula(w, h) {
    const nw = Math.max(96, (w / 4) | 0);
    const nh = Math.max(54, (h / 4) | 0);

    _seedNoise(this._nebulaSeed);

    const scale = this.config.nebulaScale;
    const octaves = this.config.nebulaDetail;
    const distortion = this.config.nebulaDistortion;
    const palette = this._activePalette;
    // Sharpness maps to the exponential falloff exponent: 0.1 → 3.0, 1.0 → 8.0
    const falloff = 3.0 + this.config.nebulaSharpness * 5.0;
    // Higher sharpness → higher density threshold (more black space)
    const threshold = 0.15 + this.config.nebulaSharpness * 0.35;

    // We generate 2 base layers + 2 hue-shifted variants for hue-shift mode
    // Indices: 0=bass base, 1=mid base, 2=bass hue-shifted, 3=mid hue-shifted
    const hueShiftMode = this.config.nebulaResponse === 'hue-shift';
    const layerCount = hueShiftMode ? 4 : 2;

    // Ensure array is large enough
    if (this._nebulaCanvases.length < layerCount) {
      this._nebulaCanvases = new Array(layerCount).fill(null);
    }

    for (let layer = 0; layer < layerCount; layer++) {
      const baseLayer = layer % 2; // 0 or 1 (bass or mid)
      const isShifted = layer >= 2;
      // Hue-shifted variants shift palette position by 0.3
      const hueOffset = isShifted ? 0.3 : 0;

      const canvas = this._nebulaCanvases[layer] = this._makeCanvas(nw, nh);
      const ctx = canvas.getContext('2d');
      const imageData = ctx.createImageData(nw, nh);
      const data = imageData.data;

      // Each base layer uses different noise offset for unique cloud shapes
      const ox = baseLayer * 1347.5 + this._nebulaSeed * 0.0013;
      const oy = baseLayer * 2891.3 + this._nebulaSeed * 0.0027;

      // Palette color positions: layer 0 uses warm palette region, layer 1 uses cool
      const baseColorT = baseLayer === 0 ? 0.15 : 0.65;

      for (let py = 0; py < nh; py++) {
        for (let px = 0; px < nw; px++) {
          let nx = px * scale + ox;
          let ny = py * scale + oy;

          // Domain distortion: warp coordinates through additional noise
          if (distortion > 0.01) {
            const dx = _perlin2(nx + 137.7, ny + 281.3) * distortion;
            const dy = _perlin2(nx + 419.2, ny + 563.8) * distortion;
            nx += dx;
            ny += dy;
          }

          // fBm density with abs() for ridged appearance (sharp cloud filaments)
          let density = _fbm2(nx, ny, octaves, 2.1, 0.48);
          density = Math.abs(density);
          // Exponential falloff (wwwtyro technique): higher falloff = sharper edges
          density = Math.exp(-falloff * density);
          // Threshold creates distinct black regions
          density = Math.max(0, density - threshold) / (1 - threshold);
          // Power curve for additional contrast
          density = density * density;

          if (density < 0.003) {
            const idx = (py * nw + px) * 4;
            data[idx] = data[idx+1] = data[idx+2] = data[idx+3] = 0;
            continue;
          }

          // Hue variation from noise cross-products (Delvar technique)
          const noiseHue = _perlin2(nx * 0.4 + 500 + baseLayer * 200, ny * 0.4 + 600) * 0.25;
          const colorT = ((baseColorT + noiseHue + hueOffset) % 1 + 1) % 1;

          // Look up palette color
          const pos = colorT * (palette.length - 1);
          const ci = Math.floor(pos);
          const cf = pos - ci;
          const c0 = palette[ci];
          const c1 = palette[Math.min(ci + 1, palette.length - 1)];
          const cr = c0[0] + (c1[0] - c0[0]) * cf;
          const cg = c0[1] + (c1[1] - c0[1]) * cf;
          const cb = c0[2] + (c1[2] - c0[2]) * cf;

          const idx = (py * nw + px) * 4;
          data[idx]     = Math.min(255, (cr * density * 2.5) | 0);
          data[idx + 1] = Math.min(255, (cg * density * 2.5) | 0);
          data[idx + 2] = Math.min(255, (cb * density * 2.5) | 0);
          data[idx + 3] = Math.min(255, (density * 220) | 0);
        }
      }

      ctx.putImageData(imageData, 0, 0);
    }

    this._nebulaGenW = w;
    this._nebulaGenH = h;
  }

  // ─── Trail fade infrastructure ────────────────────────────────────────

  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      this._trailCanvas = this._makeCanvas(canvas.width, canvas.height);
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  onConfigChange(changed) {
    if ('starsMid' in changed || 'starsHero' in changed) {
      this._activateStars();
    }
    if ('colorPalette' in changed) {
      this._selectPalette();
      this._nebulaGenW = 0; // invalidate nebula
    }
    if ('nebulaScale' in changed || 'nebulaDetail' in changed ||
        'nebulaDistortion' in changed || 'nebulaSharpness' in changed ||
        'nebulaResponse' in changed) {
      this._nebulaGenW = 0; // invalidate nebula
    }
    if ('pointStarDensity' in changed) {
      this._pointStarGenW = 0; // invalidate star field
    }
  }

  // ─── Main draw override (trail fade + layer compositing) ──────────────

  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);
    this._currentFeatures = features;

    const { ctx, canvas } = this;
    const w = canvas.width, h = canvas.height;

    // Ensure pre-rendered canvases exist
    this._ensurePreRendered(w, h);

    // Trail fade: snapshot → clear → composite at reduced opacity
    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      this._ensureTrailCanvas();
      this._trailCtx.clearRect(0, 0, w, h);
      this._trailCtx.drawImage(canvas, 0, 0);
      this.clearCanvas();
      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    // OUTSIDE trail composite: pre-rendered nebula layers
    if (this.config.showNebula) {
      this._drawNebulae(ctx, w, h);
    }

    // OUTSIDE trail composite: pre-rendered point star field
    if (this._pointStarCanvas) {
      ctx.save();
      ctx.globalAlpha = this.config.brightness;
      ctx.drawImage(this._pointStarCanvas, 0, 0);
      ctx.restore();
    }

    // Animated content (inside trail for afterimages)
    this.renderFrame(amplitudes, features);
  }

  // ─── Nebula rendering (OUTSIDE trail, per-frame modulation) ──────────

  _drawNebulae(ctx, w, h) {
    const react = this.config.audioReactivity;
    const baseAlpha = this.config.nebulaIntensity;
    const isHueShift = this.config.nebulaResponse === 'hue-shift';

    if (isHueShift) {
      // Hue-shift mode: crossfade between base color and shifted color per band.
      // Bass energy crossfades layer 0 (base) ↔ layer 2 (hue-shifted).
      // Mid energy crossfades layer 1 (base) ↔ layer 3 (hue-shifted).
      // This creates smooth color movement without brightness flashing.
      const bassShift = Math.min(1, this._sBass * react * 1.5 + this._nebulaFlash * 0.5);
      const midShift = Math.min(1, this._sMid * react * 1.5 + this._nebulaFlash * 0.5);

      // Layer 0: bass base (fades out as bass energy increases)
      if (this._nebulaCanvases[0] && baseAlpha > 0.005) {
        ctx.save();
        ctx.globalAlpha = Math.min(0.85, baseAlpha * (1 - bassShift * 0.7));
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(this._nebulaCanvases[0], 0, 0, w, h);
        ctx.restore();
      }

      // Layer 2: bass hue-shifted (fades in as bass energy increases)
      if (this._nebulaCanvases[2] && bassShift > 0.01) {
        ctx.save();
        ctx.globalAlpha = Math.min(0.85, baseAlpha * bassShift);
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(this._nebulaCanvases[2], 0, 0, w, h);
        ctx.restore();
      }

      // Layer 1: mid base (fades out as mid energy increases)
      if (this._nebulaCanvases[1] && baseAlpha > 0.005) {
        ctx.save();
        ctx.globalAlpha = Math.min(0.85, baseAlpha * (1 - midShift * 0.7));
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(this._nebulaCanvases[1], 0, 0, w, h);
        ctx.restore();
      }

      // Layer 3: mid hue-shifted (fades in as mid energy increases)
      if (this._nebulaCanvases[3] && midShift > 0.01) {
        ctx.save();
        ctx.globalAlpha = Math.min(0.85, baseAlpha * midShift);
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(this._nebulaCanvases[3], 0, 0, w, h);
        ctx.restore();
      }
    } else {
      // Brightness mode (classic): modulate alpha per frequency band
      const bassAlpha = baseAlpha * (1 + this._sBass * react * 0.5 + this._nebulaFlash);
      if (this._nebulaCanvases[0] && bassAlpha > 0.005) {
        ctx.save();
        ctx.globalAlpha = Math.min(0.85, bassAlpha);
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(this._nebulaCanvases[0], 0, 0, w, h);
        ctx.restore();
      }

      const midAlpha = baseAlpha * (1 + this._sMid * react * 0.5 + this._nebulaFlash);
      if (this._nebulaCanvases[1] && midAlpha > 0.005) {
        ctx.save();
        ctx.globalAlpha = Math.min(0.85, midAlpha);
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(this._nebulaCanvases[1], 0, 0, w, h);
        ctx.restore();
      }
    }
  }

  // ─── Main per-frame rendering ─────────────────────────────────────────

  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const w = canvas.width, h = canvas.height;
    const react = config.audioReactivity;

    this.time += 0.016;

    // Hue drift
    this._paletteHueOffset = (this._paletteHueOffset + config.hueDriftSpeed * 0.001) % 1;

    // Delta-clamped audio
    const maxDelta = 0.03;
    const rawBass = this.bassEnergy || 0;
    const rawMid = this.midEnergy || 0;
    const rawHigh = this.highEnergy || 0;
    const rawFlux = this.spectralFlux || 0;
    const rawSubBass = this.subBassEnergy || 0;
    this._sBass += Math.max(-maxDelta, Math.min(maxDelta, rawBass - this._sBass));
    this._sMid += Math.max(-maxDelta, Math.min(maxDelta, rawMid - this._sMid));
    this._sHigh += Math.max(-maxDelta, Math.min(maxDelta, rawHigh - this._sHigh));
    this._sFlux += Math.max(-maxDelta, Math.min(maxDelta, rawFlux - this._sFlux));
    this._sSubBass += Math.max(-maxDelta, Math.min(maxDelta, rawSubBass - this._sSubBass));

    const bass = this._sBass, mid = this._sMid, high = this._sHigh;
    const flux = this._sFlux, subBass = this._sSubBass;

    // Beat detection
    const beatDetected = this.beatDetected && react > 0;
    const beatConf = this.beatConfidence || 0;

    if (beatDetected) {
      this._beatFlashAlpha = Math.max(this._beatFlashAlpha, beatConf * config.beatFlashIntensity);
      this._beatSizeDecay = Math.max(this._beatSizeDecay, beatConf);
      this._beatRayDecay = Math.max(this._beatRayDecay, beatConf);
      this._nebulaFlash = Math.max(this._nebulaFlash, beatConf * 0.2);
    }

    this._beatFlashAlpha *= BEAT_FLASH_DECAY;
    if (this._beatFlashAlpha < 0.005) this._beatFlashAlpha = 0;
    this._beatSizeDecay *= 0.94;
    if (this._beatSizeDecay < 0.01) this._beatSizeDecay = 0;
    this._beatRayDecay *= 0.92;
    if (this._beatRayDecay < 0.01) this._beatRayDecay = 0;
    this._nebulaFlash *= 0.90;
    if (this._nebulaFlash < 0.005) this._nebulaFlash = 0;

    // Audio modulations
    const driftMult = 1 + subBass * react * 1.5;
    const effectiveDrift = config.driftSpeed * driftMult;
    const bassSizeBoost = 1 + bass * react * 0.6;
    const midRayMod = 1 + mid * react * 0.6;
    const twinkleSpeedMod = 1 + high * react * 0.5;
    const rayJitter = flux * react * 0.3;
    const glowOn = config.glowIntensity > 0;

    // ─── Mid stars ──────────────────────────────────────────────────────
    const midCount = Math.min(config.starsMid, MAX_MID);
    for (let i = 0; i < midCount; i++) {
      const star = this.starPool[i];
      if (!star.active) continue;

      // Drift
      star.x -= effectiveDrift * MID_SPEED;
      star.y += Math.sin(this.time * 0.3 + star.phase) * 0.05;

      // Wrap
      if (star.x < -10) { star.x = w + Math.random() * 30; star.y = Math.random() * h; }
      if (star.x > w + 40) star.x = -5;
      if (star.y < -5) star.y = h + 3;
      if (star.y > h + 5) star.y = -3;

      // Beat size response
      if (beatDetected) {
        star.sizeTarget = star.baseSize * (1.4 + beatConf * 0.8);
      }
      star.currentSize += (star.sizeTarget - star.currentSize) * 0.15;
      star.sizeTarget += (star.baseSize - star.sizeTarget) * 0.06;

      const size = star.currentSize * bassSizeBoost;

      // Twinkle
      const twinkle = Math.sin(this.time * star.twinkleSpeed * twinkleSpeedMod / config.twinklePeriod + star.phase);
      const alpha = Math.max(0.1, Math.min(1,
        star.brightness * config.brightness
        + twinkle * config.twinkleStrength * 0.3
        + this._beatSizeDecay * 0.15
      ));

      // Color
      this._getStarColor(star);
      const bri = 0.7 + star.brightness * 0.3;
      const r = Math.min(255, (this._rgb[0] * bri) | 0);
      const g = Math.min(255, (this._rgb[1] * bri) | 0);
      const b = Math.min(255, (this._rgb[2] * bri) | 0);

      if (glowOn && size >= 1.0) {
        ctx.save();
        ctx.shadowColor = `rgba(${r},${g},${b},${(alpha * 0.5).toFixed(3)})`;
        ctx.shadowBlur = config.glowIntensity * (size / 3);
      }

      ctx.fillStyle = `rgba(${r},${g},${b},${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(star.x, star.y, size, 0, TAU);
      ctx.fill();

      if (glowOn && size >= 1.0) {
        ctx.restore();
      }

      // Small aligned diffraction spikes for bright mid stars (subtle)
      if (star.brightness > 0.6 && size > 1.2) {
        const midSpikeLen = size * 3.5 * config.spikeLength;
        const midSpikeAlpha = alpha * (star.brightness - 0.6) * 2.0;
        const midSpikeCount = Number(config.spikeCount) || 4;
        const midSpikeAngle = (config.spikeAngle || 45) * (Math.PI / 180);
        const midSpikeStep = TAU / midSpikeCount;

        ctx.lineWidth = 0.5;
        for (let ri = 0; ri < midSpikeCount; ri++) {
          const angle = midSpikeAngle + ri * midSpikeStep;
          const cosA = Math.cos(angle);
          const sinA = Math.sin(angle);
          ctx.strokeStyle = `rgba(${r},${g},${b},${(midSpikeAlpha * 0.4).toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(star.x + cosA * size * 0.5, star.y + sinA * size * 0.5);
          ctx.lineTo(star.x + cosA * midSpikeLen, star.y + sinA * midSpikeLen);
          ctx.stroke();
        }
      }
    }

    // ─── Hero stars (radial glow + aligned diffraction spikes) ──────────
    // All spikes share the same global angle (simulating fixed telescope optics).
    // Spike count, angle, and taper are instrument-level properties, not per-star.
    const heroCount = Math.min(config.starsHero, MAX_HERO);
    const globalSpikeCount = Number(config.spikeCount) || 4;
    const globalSpikeAngle = (config.spikeAngle || 45) * (Math.PI / 180);
    const spikeStep = TAU / globalSpikeCount;
    const taper = config.spikeTaper; // 0–1: how quickly spikes fade along their length

    for (let i = 0; i < heroCount; i++) {
      const star = this.starPool[HERO_START + i];
      if (!star.active) continue;

      // Slow drift
      star.x -= effectiveDrift * HERO_SPEED;
      star.y += Math.sin(this.time * 0.12 + star.phase) * 0.03;

      // Wrap
      if (star.x < -40) { star.x = w + Math.random() * 60; star.y = Math.random() * h; }
      if (star.x > w + 70) star.x = -30;
      if (star.y < -30) star.y = h + 20;
      if (star.y > h + 30) star.y = -20;

      // Beat response: dramatic size and ray burst
      if (beatDetected) {
        star.sizeTarget = star.baseSize * (2.0 + beatConf * 2.5);
        star.rayTarget = star.baseRayLength * (2.5 + beatConf * 3.0);
      }
      star.currentSize += (star.sizeTarget - star.currentSize) * 0.15;
      star.sizeTarget += (star.baseSize - star.sizeTarget) * 0.06;
      star.rayLength += (star.rayTarget - star.rayLength) * 0.12;
      star.rayTarget += (star.baseRayLength - star.rayTarget) * 0.05;

      const size = star.currentSize * bassSizeBoost;
      const effectiveRayLen = star.rayLength * midRayMod * config.spikeLength
        * (1 + (Math.random() - 0.5) * rayJitter);

      // Twinkle
      const twinkle = Math.sin(this.time * star.twinkleSpeed * twinkleSpeedMod / config.twinklePeriod + star.phase);
      const alpha = Math.max(0.15, Math.min(1,
        star.brightness * config.brightness
        + twinkle * config.twinkleStrength * 0.15
        + this._beatSizeDecay * 0.25
      ));

      // Star color
      this._getStarColor(star);
      const bri = 0.8 + star.brightness * 0.2;
      const r = Math.min(255, (this._rgb[0] * bri) | 0);
      const g = Math.min(255, (this._rgb[1] * bri) | 0);
      const b = Math.min(255, (this._rgb[2] * bri) | 0);

      // 1. Radial gradient glow halo (Delvar technique)
      const curGlowRadius = star.glowRadius * bassSizeBoost * (1 + this._beatSizeDecay * 0.5);
      const glowGrad = ctx.createRadialGradient(
        star.x, star.y, size * 0.3,
        star.x, star.y, curGlowRadius
      );
      glowGrad.addColorStop(0, `rgba(${r},${g},${b},${(alpha * 0.35).toFixed(3)})`);
      glowGrad.addColorStop(0.3, `rgba(${r},${g},${b},${(alpha * 0.12).toFixed(3)})`);
      glowGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glowGrad;
      ctx.beginPath();
      ctx.arc(star.x, star.y, curGlowRadius, 0, TAU);
      ctx.fill();

      // 2. Diffraction spikes — aligned globally (telescope optics)
      // Only draw spikes if star is bright enough (dim stars have invisible spikes)
      if (star.brightness > SPIKE_BRIGHTNESS_THRESHOLD) {
        // Spike visibility scales with brightness (brighter = more prominent)
        const spikeVisibility = (star.brightness - SPIKE_BRIGHTNESS_THRESHOLD) /
          (1 - SPIKE_BRIGHTNESS_THRESHOLD);

        // Exponential taper: spike drawn as multiple line segments with decreasing alpha.
        // This mimics the real diffraction pattern: bright at core, fading exponentially.
        // Approach: radial gradient stroke for each spike ray (Delvar technique).
        const spikeLen = effectiveRayLen * spikeVisibility;
        const brightR = Math.min(255, r + 50);
        const brightG = Math.min(255, g + 50);
        const brightB = Math.min(255, b + 50);

        // Outer glow pass — wider, softer
        if (glowOn) {
          ctx.save();
          ctx.shadowColor = `rgba(${r},${g},${b},${(alpha * spikeVisibility * 0.35).toFixed(3)})`;
          ctx.shadowBlur = config.glowIntensity * 0.6;
        }

        // Draw each spike as a tapered line (segmented for exponential falloff)
        const segments = 6;
        for (let ri = 0; ri < globalSpikeCount; ri++) {
          const angle = globalSpikeAngle + ri * spikeStep;
          const cosA = Math.cos(angle);
          const sinA = Math.sin(angle);

          for (let s = 0; s < segments; s++) {
            const t0 = s / segments;
            const t1 = (s + 1) / segments;
            // Exponential falloff along spike length: bright at core → transparent at tip
            const segAlpha = alpha * spikeVisibility * Math.exp(-taper * 3.5 * t0);
            if (segAlpha < 0.01) break; // early exit for invisible segments

            const r0 = size * 0.3 + (spikeLen - size * 0.3) * t0;
            const r1 = size * 0.3 + (spikeLen - size * 0.3) * t1;

            // Outer stroke (wider, dimmer)
            ctx.strokeStyle = `rgba(${r},${g},${b},${(segAlpha * 0.4).toFixed(3)})`;
            ctx.lineWidth = Math.max(0.5, 2.5 * (1 - t0 * 0.6));
            ctx.beginPath();
            ctx.moveTo(star.x + cosA * r0, star.y + sinA * r0);
            ctx.lineTo(star.x + cosA * r1, star.y + sinA * r1);
            ctx.stroke();

            // Inner stroke (thinner, brighter)
            ctx.strokeStyle = `rgba(${brightR},${brightG},${brightB},${(segAlpha * 0.7).toFixed(3)})`;
            ctx.lineWidth = Math.max(0.3, 1.0 * (1 - t0 * 0.5));
            ctx.beginPath();
            ctx.moveTo(star.x + cosA * r0, star.y + sinA * r0);
            ctx.lineTo(star.x + cosA * r1, star.y + sinA * r1);
            ctx.stroke();
          }
        }

        if (glowOn) ctx.restore();
      }

      // 3. Star core (bright, colored)
      const brightR2 = Math.min(255, r + 50);
      const brightG2 = Math.min(255, g + 50);
      const brightB2 = Math.min(255, b + 50);
      ctx.fillStyle = `rgba(${brightR2},${brightG2},${brightB2},${Math.min(1, alpha * 1.4).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(star.x, star.y, size * 0.35, 0, TAU);
      ctx.fill();

      // 4. White-hot center
      ctx.fillStyle = `rgba(255,255,255,${(alpha * 0.8).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(star.x, star.y, size * 0.12, 0, TAU);
      ctx.fill();
    }

    // ─── Beat flash overlay ─────────────────────────────────────────────
    if (this._beatFlashAlpha > 0.005) {
      const flashT = this.spectralBrightness || 0.5;
      this._lookupPaletteRGB(flashT);
      const fr = this._rgb[0] | 0;
      const fg = this._rgb[1] | 0;
      const fb = this._rgb[2] | 0;
      ctx.fillStyle = `rgba(${fr},${fg},${fb},${(this._beatFlashAlpha * 0.1).toFixed(4)})`;
      ctx.fillRect(0, 0, w, h);
    }

    // ─── Sparkles ───────────────────────────────────────────────────────
    this._updateAndDrawSparkles(ctx, w, h, high, react);
  }

  // ─── Sparkle particle system ──────────────────────────────────────────

  _updateAndDrawSparkles(ctx, w, h, highEnergy, react) {
    const config = this.config;
    const dt = 0.016;

    // Spawn near hero stars
    const spawnCount = Math.min(MAX_SPARKLE_SPAWN,
      Math.floor(highEnergy * config.sparkleAmount * 12));

    const heroCount = Math.min(config.starsHero, MAX_HERO);
    for (let s = 0; s < spawnCount; s++) {
      if (heroCount === 0) break;
      const heroIdx = HERO_START + Math.floor(Math.random() * heroCount);
      const hero = this.starPool[heroIdx];
      if (!hero.active) continue;

      const sparkle = this.sparklePool[this._sparkleHead];
      this._sparkleHead = (this._sparkleHead + 1) % MAX_SPARKLES;

      const angle = Math.random() * TAU;
      const dist = Math.random() * hero.currentSize * 3;
      sparkle.x = hero.x + Math.cos(angle) * dist;
      sparkle.y = hero.y + Math.sin(angle) * dist;
      sparkle.vx = Math.cos(angle) * (6 + Math.random() * 12);
      sparkle.vy = Math.sin(angle) * (6 + Math.random() * 12);
      sparkle.maxLife = 0.3 + Math.random() * 0.5;
      sparkle.life = sparkle.maxLife;
      sparkle.size = 0.4 + Math.random() * 1.2;
      sparkle.alpha = 0.3 + Math.random() * 0.5;

      // Tinted white from parent star's color
      this._getStarColor(hero);
      sparkle.r = Math.min(255, ((this._rgb[0] + 220) / 2) | 0);
      sparkle.g = Math.min(255, ((this._rgb[1] + 220) / 2) | 0);
      sparkle.b = Math.min(255, ((this._rgb[2] + 220) / 2) | 0);
      sparkle.active = true;
    }

    // Update and draw
    for (let i = 0; i < MAX_SPARKLES; i++) {
      const sp = this.sparklePool[i];
      if (!sp.active) continue;

      sp.life -= dt;
      if (sp.life <= 0) { sp.active = false; continue; }

      sp.x += sp.vx * dt;
      sp.y += sp.vy * dt;
      sp.vx *= 0.97;
      sp.vy *= 0.97;

      if (sp.x < -5 || sp.x > w + 5 || sp.y < -5 || sp.y > h + 5) {
        sp.active = false;
        continue;
      }

      const lifeRatio = sp.life / sp.maxLife;
      const fadeAlpha = lifeRatio * lifeRatio * sp.alpha;
      if (fadeAlpha < 0.01) continue;

      ctx.fillStyle = `rgba(${sp.r},${sp.g},${sp.b},${fadeAlpha.toFixed(3)})`;
      if (sp.size < 1.0) {
        ctx.fillRect(sp.x, sp.y, sp.size, sp.size);
      } else {
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, sp.size, 0, TAU);
        ctx.fill();
      }
    }
  }

  // ─── Clear / reset ────────────────────────────────────────────────────

  clear() {
    super.clear();
    this.time = 0;
    this.isFirstFrame = true;
    this._beatFlashAlpha = 0;
    this._beatSizeDecay = 0;
    this._beatRayDecay = 0;
    this._nebulaFlash = 0;
    this._sBass = this._sMid = this._sHigh = this._sFlux = this._sSubBass = 0;
    this._paletteHueOffset = 0;
    this._sparkleHead = 0;

    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }

    for (let i = 0; i < MAX_SPARKLES; i++) {
      this.sparklePool[i].active = false;
    }

    // New seed for fresh nebula pattern
    this._nebulaSeed = (Math.random() * 1e8) | 0;
    this._nebulaGenW = 0;
    this._pointStarGenW = 0;

    this._activateStars();
  }
}

VisualizerRegistry.register(CosmicAurora);

export { CosmicAurora };
export default CosmicAurora;
